import {
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { readAndImportTheGreenShotExcelFile } from '@/domain/impact-co2/actions/read-and-import-the-green-shot-excel-file.action';
import {
  selectAllImpactCo2AnswersImported,
  selectIsImportingImpactCo2Answers,
} from '@/domain/impact-co2/impact-co2.selectors';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import { useDialogConfirm } from '@/ui/components/confirm-dialog/useDialogConfirm';
import { FSCard } from '@/ui/components/FSCard';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { DropOrSelectTheGreenShotFile } from './DropOrSelectTheGreenShotFile';
import { isImportMappingError } from './utils/is-mapping-error';

export function ModalImportAnswers({
  open,
  onClose,
  project,
}: {
  open: boolean;
  onClose: () => void;
  project: ProjectFestival;
}) {
  const dispatch = useAppDispatch();

  const dialogConfirm = useDialogConfirm();

  const isImporting = useAppSelector(selectIsImportingImpactCo2Answers);

  const allImportedAnswers = useAppSelector(selectAllImpactCo2AnswersImported);
  const importedAnswerCount = allImportedAnswers.length;

  const [deleteExistingAnswers, setDeleteExistingAnswers] = useState(false);

  const onExcelFileSelected = async (file: File) => {
    try {
      const params = {
        projectId: project.id,
        file: file,
        deleteExistingAnswers: deleteExistingAnswers,
      };
      await dispatch(readAndImportTheGreenShotExcelFile(params)).unwrap();
      onClose();
    } catch (error) {
      console.error(error);
      if (isImportMappingError(error)) {
        showDialogWithError(error.name, error.message);
      }
    }
  };

  const showDialogWithError = async (name: string, message: string) => {
    await dialogConfirm({
      title: name,
      description: message,
      hideCancelButton: true,
      confirmationText: 'Ok',
    });
  };

  return (
    <FSModal open={open} onClose={onClose} showCloseButton maxWidth={800}>
      <Stack spacing={3}>
        <FSModalTitle title={translate('ModalImportAnswers.modal-title')} />
        {isImporting ? (
          <InProgressView />
        ) : (
          <>
            <HowToUseMessage />
            <DropOrSelectTheGreenShotFile
              onExcelFileSelected={onExcelFileSelected}
            />
          </>
        )}
        {importedAnswerCount > 0 && (
          <DeleteExistingAnswers
            importedAnswerCount={importedAnswerCount}
            deleteExistingAnswers={deleteExistingAnswers}
            setDeleteExistingAnswers={setDeleteExistingAnswers}
          />
        )}
      </Stack>
    </FSModal>
  );
}

function DeleteExistingAnswers({
  importedAnswerCount,
  deleteExistingAnswers,
  setDeleteExistingAnswers,
}: {
  importedAnswerCount: number;
  deleteExistingAnswers: boolean;
  setDeleteExistingAnswers: (value: boolean) => void;
}) {
  return (
    <FormControlLabel
      label={translate('ModalImportAnswers.delete-existing-answers', {
        count: importedAnswerCount,
      })}
      control={
        <Checkbox
          checked={deleteExistingAnswers}
          onChange={(e) => setDeleteExistingAnswers(e.target.checked)}
        />
      }
    />
  );
}

function HowToUseMessage() {
  return (
    <FSCard
      variant="elevation"
      sx={{ bgcolor: ColorsRaw.anthracite.Warm_Anthracite_2 }}
    >
      <Stack sx={{ p: 2, gap: 2 }}>
        <Typography>
          {translate('ModalImportAnswers.how-to-use-message-1')}
        </Typography>
        <Typography>
          {translate('ModalImportAnswers.how-to-use-message-2')}
        </Typography>
      </Stack>
    </FSCard>
  );
}

function InProgressView() {
  return (
    <Stack sx={{ gap: 2, my: 2, py: 20, alignItems: 'center' }}>
      <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
        {translate('ModalImportAnswers.import-in-progress')}
      </Typography>
      <LinearProgress sx={{ width: 200 }} />
    </Stack>
  );
}
